import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { jqxGridComponent } from 'jqwidgets-ng/jqxgrid';
import { jqxNotificationComponent } from 'jqwidgets-ng/jqxnotification';
import { asyncScheduler } from 'rxjs';
import { BackendService } from 'src/app/services/backend.service';
import { CaseProfileOutputModel } from 'src/app/models/apiRequestModels';
import { toDateValue } from 'src/app/shared/dynamic-form/date-value';

declare const $: any;
declare const jqx: any;

@Component({
  selector: 'app-select-case-dialog',
  templateUrl: './select-case-dialog.component.html',
  styleUrls: ['./select-case-dialog.component.css']
})
export class SelectCaseDialogComponent implements OnInit {

  @ViewChild('selectCaseGrid', { static: false }) myGrid: jqxGridComponent;
  @ViewChild('msgNotification', { static: false }) msgNotification: jqxNotificationComponent;

  // Hearing date range lives above the grid (Job List style); the rest of the filters are per-column
  // filters read at request time in readAllFilters().
  hearingDateFrom = '';
  hearingDateTo = '';
  hearingDateFromValue: Date | null = null;
  hearingDateToValue: Date | null = null;
  // jqxDateTimeInput's own default bounds; restoring them lifts a limit, since the widget ignores null.
  private static readonly EARLIEST_HEARING_DATE = new Date(1900, 0, 1);
  private static readonly LATEST_HEARING_DATE = new Date(2100, 0, 1);
  fromMaxDate = SelectCaseDialogComponent.LATEST_HEARING_DATE;
  toMinDate = SelectCaseDialogComponent.EARLIEST_HEARING_DATE;
  caseStatus = '';
  caseStatusOptions: string[] = ['Created', 'Scheduled', 'In Progress', 'Completed', 'Cancelled'];
  caseProfiles: CaseProfileOutputModel[] = [];
  orgExtId = '';
  // Court Room / Court Location default to the recording's context; pre-filled into the column filters.
  private defaultCourtRoom = '';
  private defaultCourtLocation = '';
  private statusFilterWidget: any = null;

  // Shown in the grid body when a filter returns no rows, so an empty result reads as
  // "nothing matched" rather than looking like the filter/grid is broken.
  gridLocalization: any = { emptydatastring: 'No cases found for the selected filters.' };

  // Per-column filters (Job List style): Case Number / Case Profile / Court Room / Court Location use
  // the default 'input' box; Case Status uses a prepopulated native <select>; Hearing Date is filtered
  // by the range above the grid.
  columns: any[] = [
    { text: 'Case Number', datafield: 'caseNo', width: '14%', align: 'center', cellsalign: 'left', filtertype: 'input' },
    { text: 'Case Profile', datafield: 'caseProfileName', width: '22%', align: 'center', cellsalign: 'left', filtertype: 'input' },
    { text: 'Hearing Date', datafield: 'hearingDate', width: '16%', align: 'center', cellsalign: 'center', filterable: false },
    { text: 'Court Room', datafield: 'courtRoom', width: '14%', align: 'center', cellsalign: 'left', filtertype: 'input' },
    { text: 'Court Location', datafield: 'courtLocation', width: '18%', align: 'center', cellsalign: 'left', filtertype: 'input' },
    {
      text: 'Case Status', datafield: 'caseStatus', width: '16%', align: 'center', filtertype: 'checkedlist',
      createfilterwidget: (column: any, columnElement: any, widget: any) => this.createStatusFilterWidget(column, columnElement, widget)
    }
  ];

  // Server-side grid (mirrors the Case List): the grid requests one page at a time from the API
  // (pagenum/pagesize/sortdatafield/sortorder + the filters injected by formatData below).
  source: any = {
    datatype: 'json',
    datafields: [
      { name: 'id', type: 'number' },
      { name: 'caseNo', type: 'string' },
      { name: 'caseProfileId', type: 'number' },
      { name: 'caseProfileName', type: 'string' },
      { name: 'hearingDate', type: 'string' },
      { name: 'courtRoom', type: 'string' },
      { name: 'courtLocation', type: 'string' },
      { name: 'caseStatus', type: 'string' }
    ],
    root: 'rows',
    id: 'id',
    sortcolumn: 'hearingDate',
    sortdirection: 'asc',
    loader: false,
    beforeprocessing: (data: any) => {
      this.source.totalrecords = data.totalRows;
      const results = data.rows || [];
      for (const index in results) {
        const val = results[index];
        if (val.hearingDate) {
          // hearing_date has no offset, so toDateValue reads it as local wall-clock time (no UTC shift).
          const d = toDateValue(val.hearingDate);
          val.hearingDate = d ? (this.datePipe.transform(d, 'yyyy-MM-dd HH:mm') || '') : '';
        }
      }
    },
    filter: () => {
      // A column filter changed - re-query the server (formatData injects the mapped params).
      this.myGrid.updatebounddata('filter');
    },
    sort: () => {
      this.myGrid.updatebounddata('sort');
    }
  };

  dataAdapter: any = new jqx.dataAdapter(this.source, {
    autoBind: false,
    // Single choke point: map the column filters + hearing-date range onto the backend's named
    // query params on every request (initial load, sort, page, and filter changes).
    formatData: (data: any) => Object.assign(data || {}, this.readAllFilters()),
    beforeSend: function (jqXHR: any) {
      this.token = localStorage.getItem('token');
      if (this.token != null) {
        jqXHR.setRequestHeader('Authorization', 'Bearer ' + this.token);
      }
    },
    contentType: 'application/json; charset=utf-8',
    loadError: () => {
      this.sendNotification('Unable to load cases. Please refresh and try again.', 'error');
    }
  });

  rendergridrows = (params: any): any => {
    return params.data;
  }

  constructor(
    private backendService: BackendService,
    private datePipe: DatePipe,
    public dialogRef: MatDialogRef<SelectCaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    this.defaultCourtRoom = (data && data.courtRoom) ? data.courtRoom : '';
    this.defaultCourtLocation = (data && data.courtLocation) ? data.courtLocation : '';
    this.orgExtId = (data && data.orgExtId) ? data.orgExtId : '';
  }

  ngOnInit(): void {
    const today = new Date();
    const sevenDaysFromToday = new Date(today);
    sevenDaysFromToday.setDate(sevenDaysFromToday.getDate() + 7);
    this.hearingDateFrom = this.datePipe.transform(today, 'yyyy-MM-dd') || '';
    this.hearingDateTo = this.datePipe.transform(sevenDaysFromToday, 'yyyy-MM-dd') || '';
    this.hearingDateFromValue = today;
    this.hearingDateToValue = sevenDaysFromToday;
    this.updateHearingDateBounds();

    this.backendService.getActiveCaseProfiles(this.orgExtId).subscribe((res: any) => {
      this.caseProfiles = (res && res.data) ? res.data : (res || []);
    }, () => {
      this.caseProfiles = [];
    });
    // Defer the first load until the grid ViewChild is available.
    asyncScheduler.schedule(() => {
      if (this.defaultCourtRoom || this.defaultCourtLocation) {
        this.applyContextDefaults();   // pre-fill the court column filters -> triggers a filtered load
      } else {
        this.loadData();
      }
    });
    // The MatDialog open animation grows the container AFTER the grid first measures its
    // (percentage) width; re-render once opened so the grid fills the full width.
    this.dialogRef.afterOpened().subscribe(() => {
      asyncScheduler.schedule(() => {
        if (this.myGrid) { this.myGrid.render(); }
      });
    });
  }

  // Read the applied per-column filter-row values (case no, profile name, court room/location).
  private readColumnFilters(): any {
    const out: any = { caseNo: '', caseProfileName: '', courtRoom: '', courtLocation: '' };
    if (!this.myGrid) { return out; }
    let info: any[] = [];
    try { info = this.myGrid.getfilterinformation() || []; } catch (e) { return out; }
    (info || []).forEach((col: any) => {
      const field = col.datafield || col.filtercolumn;
      let conditions: any[] = [];
      try { conditions = col.filter.getfilters(); } catch (e) { conditions = []; }
      const values = (conditions || [])
        .map((c: any) => c && c.value)
        .filter((v: any) => v !== null && v !== undefined && ('' + v).trim() !== '');
      if (!values.length) { return; }
      const value = '' + values[0];
      switch (field) {
        case 'caseNo': out.caseNo = value; break;
        case 'caseProfileName': out.caseProfileName = value; break;
        case 'courtRoom': out.courtRoom = value; break;
        case 'courtLocation': out.courtLocation = value; break;
      }
    });
    return out;
  }

  // Full named-filter payload (column filters + status widget + hearing-date range), injected onto
  // every grid request via the adapter's formatData.
  private readAllFilters(): any {
    const cf = this.readColumnFilters();
    return {
      caseNo: cf.caseNo,
      caseProfileName: cf.caseProfileName,
      courtRoom: cf.courtRoom,
      courtLocation: cf.courtLocation,
      caseStatus: this.caseStatus || '',
      hearingDateFrom: this.hearingDateFrom || '',
      hearingDateTo: this.hearingDateTo || '',
      // Scope the search to the recording's organisation (passed in from the recording page); the
      // backend resolves it via CaseAccess, so a host admin sees the cases for the org being recorded.
      orgExtId: this.orgExtId || ''
    };
  }

  onGridFilter(event: any): void {
    // The grid re-queries automatically (source.filter); formatData injects the mapped params.
  }

  private normalizeDateInput(value: any): string {
    if (!value) { return ''; }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) { return ''; }
    return this.datePipe.transform(d, 'yyyy-MM-dd') || '';
  }

  onHearingDateFromChanged(event: any): void {
    const date = event?.args?.date ?? null;
    this.hearingDateFrom = this.normalizeDateInput(date);
    this.hearingDateFromValue = date ? new Date(date) : null;
    this.updateHearingDateBounds();
    this.reload();
  }

  onHearingDateToChanged(event: any): void {
    const date = event?.args?.date ?? null;
    this.hearingDateTo = this.normalizeDateInput(date);
    this.hearingDateToValue = date ? new Date(date) : null;
    this.updateHearingDateBounds();
    this.reload();
  }

  private updateHearingDateBounds(): void {
    this.toMinDate = this.hearingDateFromValue
      ? this.startOfDay(this.hearingDateFromValue) : SelectCaseDialogComponent.EARLIEST_HEARING_DATE;
    this.fromMaxDate = this.hearingDateToValue
      ? this.startOfDay(this.hearingDateToValue) : SelectCaseDialogComponent.LATEST_HEARING_DATE;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  reload(): void {
    this.source.url = this.backendService.getcaseswithfilterurl();
    if (this.myGrid) { this.myGrid.updatebounddata('filter'); }
  }

  loadData(): void {
    // Loading feedback comes from the grid's own (localized) load element; we deliberately do NOT use
    // the global full-screen NgxSpinner here (its overlay renders above the dialog and can intercept
    // clicks on the filters, making them appear "not working").
    this.source.url = this.backendService.getcaseswithfilterurl();
    if (this.myGrid) { this.myGrid.updatebounddata(); }
  }

  // Pre-fill the Court Room / Court Location column filters from the recording context; applying the
  // filters triggers the (filtered) initial load via source.filter.
  private applyContextDefaults(): void {
    if (!this.myGrid) { this.loadData(); return; }
    this.source.url = this.backendService.getcaseswithfilterurl();
    const addTextFilter = (field: string, value: string) => {
      if (!value) { return; }
      try {
        const fg = new jqx.filter();
        fg.addfilter(1, fg.createfilter('stringfilter', value, 'contains'));
        this.myGrid.addfilter(field, fg);
      } catch (e) { }
    };
    addTextFilter('courtRoom', this.defaultCourtRoom);
    addTextFilter('courtLocation', this.defaultCourtLocation);
    try { this.myGrid.applyfilters(); } catch (e) { this.loadData(); }
  }

  // Prepopulated Case Status column filter as a native <select> (keyboard-accessible; the grid has
  // [keyboardnavigation]="false" so its keys aren't swallowed).
  createStatusFilterWidget(column: any, columnElement: any, widget: any): void {
    this.statusFilterWidget = widget;
    const select = document.createElement('select');
    select.className = 'case-status-filter';
    select.setAttribute('title', 'Filter by Case Status');
    select.style.width = '100%';
    select.style.height = '24px';
    select.style.boxSizing = 'border-box';
    select.style.border = '1px solid #c4c4c4';
    select.style.borderRadius = '3px';
    select.style.background = '#fff';
    select.style.padding = '0 2px';
    ['All'].concat(this.caseStatusOptions).forEach(label => {
      const opt = document.createElement('option');
      opt.value = (label === 'All') ? '' : label;
      opt.textContent = label;
      select.appendChild(opt);
    });
    select.tabIndex = 0;
    select.value = this.caseStatus || '';
    select.addEventListener('change', () => {
      this.caseStatus = select.value || '';
      this.reload();
    });
    select.addEventListener('keydown', (e: any) => { e.stopPropagation(); });
    select.addEventListener('mousedown', (e: any) => { e.stopPropagation(); });
    try { widget.empty(); } catch (e) { }
    try { widget.append(select); } catch (e) { if (columnElement && columnElement.length) { columnElement[0].appendChild(select); } }
  }

  // Grid toolbar (Job List style): flat, borderless PNG-icon buttons in the grid's toolbar bar.
  renderGridToolbar = (toolbar: any): void => {
    const host = (toolbar && toolbar[0]) ? toolbar[0] : toolbar;
    if (!host) { return; }
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;align-items:center;justify-content:flex-start;gap:4px;width:100%;height:100%;padding:0 6px;box-sizing:border-box;';
    const makeBtn = (iconFile: string, label: string, onClick: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.title = label;
      b.setAttribute('aria-label', label);
      b.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;height:32px;width:36px;padding:0;border:none;background:transparent;cursor:pointer;border-radius:3px;';
      b.innerHTML = '<img src="assets/img/icon/' + iconFile + '" alt="' + label + '" style="height:20px;width:auto;display:block;pointer-events:none;" />';
      b.addEventListener('mouseenter', () => { b.style.background = 'rgba(24,126,177,0.12)'; });
      b.addEventListener('mouseleave', () => { b.style.background = 'transparent'; });
      b.addEventListener('click', (e) => { e.preventDefault(); onClick(); });
      return b;
    };
    wrap.appendChild(makeBtn('refresh.png', 'Refresh', () => this.loadData()));
    wrap.appendChild(makeBtn('clear.png', 'Reset Filters', () => this.reset()));
    host.appendChild(wrap);
  }

  reset(): void {
    this.hearingDateFrom = '';
    this.hearingDateTo = '';
    this.hearingDateFromValue = null;
    this.hearingDateToValue = null;
    this.updateHearingDateBounds();
    this.caseStatus = '';
    // Reset the in-grid status <select> back to "All" (setting value fires no change event).
    try {
      const sel = this.statusFilterWidget && this.statusFilterWidget.find ? this.statusFilterWidget.find('select')[0] : null;
      if (sel) { sel.value = ''; }
    } catch (e) { }
    try { this.myGrid.clearfilters(); } catch (e) { }
    // Restore the Court Room / Court Location context defaults from the recording page.
    if (this.defaultCourtRoom || this.defaultCourtLocation) {
      this.applyContextDefaults();
    } else {
      this.loadData();
    }
  }

  onSelect(): void {
    const index = this.myGrid.getselectedrowindex();
    if (index == null || index < 0) {
      this.sendNotification('Please select a case.', 'error');
      return;
    }
    const row = this.myGrid.getrowdata(index);
    if (!row || row.id == null) {
      this.sendNotification('Please select a valid case.', 'error');
      return;
    }
    this.dialogRef.close(row.id);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private resolveError(err: any, fallback: string): string {
    if (err && err.error && err.error.message) { return err.error.message; }
    if (err && err.message) { return err.message; }
    return fallback;
  }

  sendNotification(message: string, messageType: string): void {
    this.msgNotification.elementRef.nativeElement.querySelector('.jqx-notification-content').innerHTML = message;
    this.msgNotification.template(messageType);
    this.msgNotification.open();
  }
}
